import React, { useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { message } from "antd";
import {
  ArrowLeftOutlined,
  ShareAltOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import Moment from "moment";
import { findCoursePublicByIdRequest } from "services/course";
import { CourseFeatures } from "components";
import useFetch from "hooks/useFetch";
import { Colors } from "configs";
import Filters from "utils/Filters";
import { useAuthAuthenticated } from "store/hooks/auth";
import {
  Page,
  Banner,
  BannerInner,
  TopBar,
  TopButton,
  BannerTitle,
  BannerSummary,
  Content,
  Card,
  SideColumn,
  SectionTitle,
  Description,
  PriceRow,
  Price,
  OldPrice,
  DiscountNote,
  BuyButton,
  SecureNote,
} from "./styles";

const CoursePublicDetail = () => {
  const history = useHistory();
  const { id } = useParams();
  const authenticated = useAuthAuthenticated();
  const [{ data }] = useFetch(findCoursePublicByIdRequest, id, true, [], null);

  const discountedPrice = useMemo(() => {
    return data.pr_desconto
      ? parseInt(data.vl_curso) -
          (parseInt(data.vl_curso) * parseInt(data.pr_desconto)) / 100
      : data.vl_curso;
  }, [data]);

  const isFree = data.vl_curso === 0;
  const hasDiscount = Boolean(data.pr_desconto);

  const availability = useMemo(() => {
    const now = Moment(new Date());
    if (data.fg_ativo === false) {
      return { enabled: false, label: "Indisponível" };
    }
    if (data.dt_inicio && now.isBefore(data.dt_inicio)) {
      return {
        enabled: false,
        label: `Inscrições a partir de ${Moment(data.dt_inicio).format(
          "DD/MM/YYYY"
        )}`,
      };
    }
    if (data.dt_fim && now.isAfter(data.dt_fim)) {
      return { enabled: false, label: "Inscrições encerradas" };
    }
    return {
      enabled: true,
      label: isFree ? "Inscrever-se grátis" : "Comprar",
    };
  }, [data, isFree]);

  function handleBuy() {
    if (!availability.enabled) return;
    if (authenticated) {
      history.push(`/course-buy/${id}`);
    } else {
      history.push(`/login?returnUrl=/course-buy/${id}`);
    }
  }

  function handleShare() {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: data.ds_titulo, url }).catch(() => {});
      return;
    }
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      message.success("Link copiado para a área de transferência!");
    }
  }

  return (
    <Page>
      <Banner image={data.ds_imagem}>
        <TopBar>
          <TopButton onClick={() => history.push("/")}>
            <ArrowLeftOutlined /> Voltar
          </TopButton>
          <TopButton onClick={handleShare}>
            <ShareAltOutlined /> Compartilhar
          </TopButton>
        </TopBar>
        <BannerInner>
          <BannerTitle>{data.ds_titulo}</BannerTitle>
          {data.ds_resumo && <BannerSummary>{data.ds_resumo}</BannerSummary>}
        </BannerInner>
      </Banner>

      <Content>
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          <Card>
            <SectionTitle>Descrição</SectionTitle>
            <Description>{data.ds_curso}</Description>
          </Card>
        </div>

        <SideColumn>
          <Card>
            {isFree ? (
              <PriceRow>
                <Price>Curso livre</Price>
              </PriceRow>
            ) : (
              <>
                <PriceRow>
                  <Price>{Filters.convertMoneyTextMask(discountedPrice)}</Price>
                  {hasDiscount && (
                    <OldPrice>
                      {Filters.convertMoneyTextMask(data.vl_curso)}
                    </OldPrice>
                  )}
                </PriceRow>
                {hasDiscount && (
                  <DiscountNote>
                    {`Desconto de ${data.pr_desconto}%`}
                    {data.dt_validade_desconto &&
                      ` · válido até ${Moment(
                        data.dt_validade_desconto
                      ).format("DD/MM/YYYY")}`}
                  </DiscountNote>
                )}
              </>
            )}
            <BuyButton
              onClick={handleBuy}
              disabled={!availability.enabled}
              style={
                availability.enabled
                  ? undefined
                  : { background: "#c5d0ce", cursor: "not-allowed" }
              }
            >
              {availability.label}
            </BuyButton>
            <SecureNote>
              <SafetyOutlined style={{ color: Colors.primary }} />
              {authenticated
                ? "Pagamento seguro"
                : "Cadastro rápido na hora da compra"}
            </SecureNote>
          </Card>

          <Card>
            <CourseFeatures data={data} />
          </Card>
        </SideColumn>
      </Content>
    </Page>
  );
};

export default CoursePublicDetail;
